import React from "react";
import { Button, Spinner } from "reactstrap";

/**
 * Sticky bottom action bar.
 *
 *  Pre-submit author flow (CR in "Saved" state):
 *    • Save & Finish Later — always enabled (draft save).
 *    • Submit CR — fires the initial submission cascade. Validation lives in
 *      ContractReviewForm.handleSubmit; no popup interrogation.
 *
 *  Post-submit responsible flow (CR "In Progress"):
 *    • Save Draft — always enabled. Saves your current partial work. Useful
 *      when you've filled SOME of your assigned rows and want to keep going
 *      later (or hand off to a teammate).
 *    • Save & Close — DISABLED until every row assigned to YOU is complete.
 *      Once enabled, click it to write your finished section and navigate out.
 *      That save is what advances the legacy cascade (Stage 2 / Stage 3).
 *
 *  Ready / archived: Print + Archive + Won/Lost are handled here / WonLostBar.
 */
export default function BottomActions({
    mode,
    onSubmit,
    onSaveLater,
    onSaveDraft,
    onSaveClose,
    onSaveAssignments,
    assignmentsDirty = false,
    onPrint,
    onCancel,
    onArchive,
    onDelete,
    isSaving,
    isSubmitting,
    isCancelling,
    isArchiving,
    isPrinting,
    isDeleting,
    myRowsComplete = true,
    myRowsCount = 0,
    requiresDisposition = false,
}) {
    // Go-live parity (2026-09-24): S11 — Submit only for the author / the author's supervisor
    // (it used to show to anyone viewing a saved CR, and the server then refused it).
    const showSubmit     = !!mode.canSubmit;
    const showSaveLater  = mode.isNew || mode.isSaved;
    const showSaveDraft  = mode.isInProgress;
    const showSaveClose  = mode.isInProgress;
    const showPrint      = !mode.isNew && (mode.isInProgress || mode.isReady || mode.isArchived);
    const showCancelCr   = mode.canCancel;
    // Round 8 — for a quote-driven CR, the FINAL step is "Mark Won/Lost &
    // Archive" via the disposition bar (legacy requires a disposition before a
    // quote CR can be archived; the backend returns 400 otherwise). In that
    // case we hide the plain "Approve & Archive (Final)" button so the user
    // isn't shown two finalize buttons where one always errors. For non-quote
    // CRs the disposition bar isn't shown and this button IS the finalize step.
    const showArchive    = mode.canArchive && !mode.quoteOnly && !requiresDisposition;
    const showDelete     = mode.canDeleteCr;
    // Re-assignment save: the author may change who is responsible even after
    // submission (in In-Progress or Ready). Those states don't otherwise show a
    // plain save, so expose one explicitly. Reuses the draft-save (no lifecycle
    // change); the backend emails any newly-assigned person on save.
    const showSaveAssign = mode.isAuthor && (mode.isInProgress || mode.isReady) && !mode.isArchived && !!onSaveAssignments && assignmentsDirty;

    // Save & Close is the "I'm done with my section" action — gated by
    // whether all rows assigned to the current user are complete.
    const saveCloseDisabled = isSaving || !myRowsComplete;
    const saveCloseTooltip  = myRowsCount === 0
        ? "You have no rows assigned to you on this CR."
        : (!myRowsComplete
            ? "Fill in every row assigned to you (Response + Record Name trio where required) to enable this button."
            : undefined);

    return (
        <div
            className="d-flex justify-content-end gap-2 flex-wrap py-3 mt-3"
            style={{ position: "sticky", bottom: 0, background: "#fff", borderTop: "1px solid #eee", zIndex: 5 }}
        >
            {showDelete && (
                <Button color="danger" outline onClick={onDelete} disabled={isDeleting}>
                    {isDeleting
                        ? <Spinner size="sm" />
                        : <i className="ri-delete-bin-line me-1" />}
                    Delete
                </Button>
            )}
            {showCancelCr && (
                <Button color="warning" outline onClick={onCancel} disabled={isCancelling}>
                    {isCancelling ? <Spinner size="sm" /> : <i className="ri-close-circle-line me-1" />}
                    Cancel CR
                </Button>
            )}
            {showArchive && (
                <Button color="success" onClick={onArchive} disabled={isArchiving}
                    title="All responses are complete. Approve the contract review and archive it — this is the final step.">
                    {isArchiving ? <Spinner size="sm" /> : <i className="ri-check-double-line me-1" />}
                    Approve &amp; Archive (Final)
                </Button>
            )}
            {showSaveAssign && (
                <Button color="info" outline onClick={onSaveAssignments} disabled={isSaving}
                    title="Save changes to who is responsible. Newly assigned people are emailed.">
                    {isSaving ? <Spinner size="sm" /> : <i className="ri-user-shared-line me-1" />}
                    Save Assignments
                </Button>
            )}
            {showPrint && (
                <Button color="light" onClick={onPrint} disabled={isPrinting}>
                    {isPrinting ? <Spinner size="sm" /> : <i className="ri-printer-line me-1" />}
                    Print
                </Button>
            )}
            {showSaveLater && (
                <Button color="light" onClick={onSaveLater} disabled={isSaving}>
                    {isSaving ? <Spinner size="sm" /> : <i className="ri-draft-line me-1" />}
                    Save &amp; Finish Later
                </Button>
            )}
            {showSaveDraft && (
                <Button color="light" onClick={onSaveDraft} disabled={isSaving}
                    title="Save your current partial work without closing.">
                    {isSaving ? <Spinner size="sm" /> : <i className="ri-draft-line me-1" />}
                    Save Draft
                </Button>
            )}
            {showSaveClose && (
                <Button
                    color="primary"
                    onClick={onSaveClose}
                    disabled={saveCloseDisabled}
                    title={saveCloseTooltip}
                >
                    {isSaving ? <Spinner size="sm" /> : <i className="ri-save-line me-1" />}
                    Save &amp; Close
                </Button>
            )}
            {showSubmit && (
                <Button color="success" onClick={onSubmit} disabled={isSubmitting || isSaving}>
                    {isSubmitting || isSaving ? <Spinner size="sm" /> : <i className="ri-send-plane-line me-1" />}
                    Submit CR
                </Button>
            )}
        </div>
    );
}